import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AppPage, AppContent, useFeatureToggle } from '@css-xp/base';
import { SIDEBAR, AggregatedSoldToAddress } from '@css-xp/ppc-sold-to-address-table';
import { AnalyticsEventSource } from '@atlassian/temp-analytics';
import ExportIcon from '@atlaskit/icon/glyph/export';
import { PageBackground, PageBackgroundV2 } from '@css-xp/page-background';
import { Background } from '@css-xp/page-background/src/background-image';
import { ViewLinkedSubscriptionsSidebar } from '@css-xp/billing-v2';
import { ShipToPartySidebar } from '@css-xp/ppc-ship-to-party-sidebar';
import { PPC_PAGE_TITLE, ENABLE_DESIGN_ITERATION_1, routePaths, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { useTitle } from '@css-xp/ppc-custom-hooks';
import { ScreenEventAnalytics } from '@css-xp/ppc-common';
import { DarkLoadingButton } from '@css-xp/ppc-dark-button';
import { PartnerSearchOptions, PartnerSearchContext } from '@css-xp/ppc-graphtypes';
import './customer-address-page-v2.scss';
//need to be removed once M2.0 Feature flag is removed
import './customer-address-page.scss';
import Constants from './constants';
import { EditCustomerNamePane } from './components/sidebar/edit-customer-name-pane';
import { useCustomersListCSV } from './hooks/use-customers-list-csv';

const allowedSidebars = {
  [SIDEBAR.EDIT_SOLD_TO_ADDRESS]: ShipToPartySidebar,
  [SIDEBAR.ADD_SOLD_TO_ADDRESS]: ShipToPartySidebar,
  [SIDEBAR.VIEW_LINKED_SUBSCRIPTIONS]: ViewLinkedSubscriptionsSidebar,
  [SIDEBAR.EDIT_CUSTOMER_NAME]: EditCustomerNamePane,
};

export const CustomerAddressPage = () => {
  const { txaId } = useParams<{ txaId: string }>();
  useTitle(PPC_PAGE_TITLE.CUSTOMERS);
  const [isEnabled] = useFeatureToggle();

  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isSoldToAddressArchivalEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ARCHIVE_SOLD_TO_ADDRESS);
  const isDuplicateCustomerNameEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DUPLICATE_CUSTOMER_NAME);
  const isRedesignFastFollowEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_REDESIGN_FAST_FOLLOW);

  const initialCsvSearchOptions: PartnerSearchOptions = {
    query: '',
    searchContext: PartnerSearchContext.ALL,
  };

  const [csvSearchOptions, setCsvSearchOptions] = useState<PartnerSearchOptions>(initialCsvSearchOptions);
  const [isExportCsvButtonDisabled, setIsExportCsvButtonDisabled] = useState<boolean>(false);

  const { downloadCustomersListCSV, customersListLoading } = useCustomersListCSV(
    txaId,
    csvSearchOptions,
    isSoldToAddressArchivalEnabled,
    isRedesignFastFollowEnabled
  );

  const renderSoldToAddressGrouped = () => {
    return (
      <AggregatedSoldToAddress
        showAddAddress={true}
        txaId={txaId}
        hideActions={true}
        hideAddFromBillToAddress={true}
        hidePricingEligibility={true}
        updateCsvSearchOptions={setCsvSearchOptions}
        setIsExportCsvButtonDisabled={setIsExportCsvButtonDisabled}
        shouldRenderNewTable={isDesignIteration1Enabled}
        downloadCustomersListCSV={downloadCustomersListCSV}
        customersListLoading={customersListLoading}
        isSoldToAddressArchivalEnabled={isSoldToAddressArchivalEnabled}
        isDuplicateCustomerNameEnabled={isDuplicateCustomerNameEnabled}
      />
    );
  };

  const renderActions = () => {
    return (
      <DarkLoadingButton
        appearance="primary"
        onClick={() => {
          downloadCustomersListCSV();
        }}
        testId="export-invoices-as-csv"
        isLoading={customersListLoading}
        isDisabled={isExportCsvButtonDisabled}
        iconBefore={<ExportIcon label="Export icon" size="small" />}
      >
        Export as CSV
      </DarkLoadingButton>
    );
  };

  const renderAppPage = () => {
    return (
      <AppPage className="app-page-v2 customer-address-page" allowedSidebars={allowedSidebars}>
        <ScreenEventAnalytics
          event={{
            name: AnalyticsEventSource.PARTNER_CUSTOMER_ADDRESS_SCREEN,
            attributes: {},
          }}
        />
        <AppContent>
          <section className="ph-50">{renderSoldToAddressGrouped()}</section>
        </AppContent>
      </AppPage>
    );
  };

  if (isDesignIteration1Enabled) {
    const breadcrumbsItems = [
      {
        title: 'Home',
        href: routePaths.home,
      },
      { title: 'Customers', href: routePaths.customerAddresses },
    ];

    return (
      <PageBackgroundV2 heading={Constants.HEADING} breadcrumbsProps={{ items: breadcrumbsItems }}>
        {renderAppPage()}
      </PageBackgroundV2>
    );
  }

  return (
    <PageBackground
      heading={Constants.HEADING}
      imageKey={Background.CUSTOMER_ADDRESS_BACKGROUND}
      className="page-background customer-address-v2"
      actions={renderActions()}
    >
      {renderAppPage()}
    </PageBackground>
  );
};
